import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPickupCouriersRequest,
  getPickupRequestsRequest,
  createPickupRequestRequest,
  getEligibleOrdersRequest,
  assignOrdersToPickupRequest,
  requestManifestRequest,
  getPickupWarehousesRequest,
  createManualPickupRequestRequest,
} from './pickupRequestsApi';
import {
  PICKUP_COURIER,
  buildManualPickupPayload,
  buildPickupRequestPayload,
  pickupFieldErrorsFromApi,
} from './pickupRequestsUtils';
import { COUNTRY_ISO2 } from '../geo/geoConstants';
import { extractApiError } from '../../utils/errors';

/*
 * One thunk per function of the legacy `usePickupRequests` hook, with its
 * messages. Assigning orders and requesting a manifest reload the pickup
 * requests afterwards, as the hook did.
 */

/**
 * Only the filters that carry a value travel, as in the hook. `status=all`
 * always does: without it the backend leaves out the failed requests.
 */
export const buildPickupFilterParams = ({ courier, date_from, date_to }) => {
  const params = { status: 'all' };
  if (courier) params.courier = courier;
  if (date_from) params.date_from = date_from;
  if (date_to) params.date_to = date_to;
  return params;
};

export const fetchPickupCouriers = createAsyncThunk(
  'pickupRequests/fetchCouriers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await getPickupCouriersRequest();
      return data;
    } catch (error) {
      console.error('Error al cargar couriers disponibles:', error);
      return rejectWithValue('Error al cargar los couriers disponibles.');
    }
  }
);

export const fetchPickupRequests = createAsyncThunk(
  'pickupRequests/fetchPickupRequests',
  async (_, { getState, rejectWithValue }) => {
    try {
      const params = buildPickupFilterParams(getState().pickupRequests.filters);
      const { data } = await getPickupRequestsRequest(params);
      return data;
    } catch (error) {
      return rejectWithValue('Error al cargar las solicitudes de retiro.');
    }
  }
);

export const fetchEligibleOrders = createAsyncThunk(
  'pickupRequests/fetchEligibleOrders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const params = buildPickupFilterParams(getState().pickupRequests.filters);
      const { data } = await getEligibleOrdersRequest(params);
      return data;
    } catch (error) {
      return rejectWithValue('Error al cargar los pedidos elegibles.');
    }
  }
);

export const fetchPickupWarehouses = createAsyncThunk(
  'pickupRequests/fetchWarehouses',
  async (courier = PICKUP_COURIER, { rejectWithValue }) => {
    try {
      const { data } = await getPickupWarehousesRequest(courier);
      return data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'Error al cargar las bodegas.'));
    }
  }
);

/**
 * Books the pickup with the courier, from the form.
 *
 * Rejects with `{message, fieldErrors, pickup}`. `pickup` comes when the
 * courier refused: the backend keeps that request as failed, so it belongs in
 * the list too.
 *
 * @param {import('./pickupRequestsUtils').PickupRequestForm} form
 */
export const createPickupRequest = createAsyncThunk(
  'pickupRequests/createPickupRequest',
  async (form, { getState, rejectWithValue }) => {
    try {
      const regions = getState().geo?.regionsByCountry?.[COUNTRY_ISO2.CL] || [];
      const { data } = await createPickupRequestRequest(buildPickupRequestPayload(form, regions));
      return data;
    } catch (error) {
      const data = error?.response?.data;
      return rejectWithValue({
        message: extractApiError(error, 'Error al crear la solicitud de retiro.'),
        fieldErrors: pickupFieldErrorsFromApi(data?.errors),
        pickup: data?.pickup || null,
      });
    }
  }
);

/**
 * Stores a pickup the courier's support created by phone, without calling the
 * courier. Rejects with `{message, fieldErrors}`.
 *
 * @param {import('./pickupRequestsUtils').ManualPickupForm} form
 */
export const registerManualPickup = createAsyncThunk(
  'pickupRequests/registerManualPickup',
  async (form, { rejectWithValue }) => {
    try {
      const { data } = await createManualPickupRequestRequest(buildManualPickupPayload(form));
      return data;
    } catch (error) {
      return rejectWithValue({
        message: extractApiError(error, 'Error al registrar el retiro.'),
        fieldErrors: pickupFieldErrorsFromApi(error?.response?.data?.errors),
      });
    }
  }
);

/**
 * @param {{pickupId: number, orderIds: number[]}} params
 * @returns {Promise<{detail: string, manifest_url: string|null}>}
 */
export const assignOrdersToPickup = createAsyncThunk(
  'pickupRequests/assignOrders',
  async ({ pickupId, orderIds }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await assignOrdersToPickupRequest(pickupId, orderIds);
      await dispatch(fetchPickupRequests());
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.detail || 'Error al asignar los pedidos.');
    }
  }
);

/** @returns {Promise<{manifest_url: string}>} */
export const requestManifest = createAsyncThunk(
  'pickupRequests/requestManifest',
  async (pickupId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await requestManifestRequest(pickupId);
      await dispatch(fetchPickupRequests());
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.detail || 'Error al obtener el manifiesto.');
    }
  }
);
